import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Search, 
  UserCheck,
  Mail,
  Phone,
  BookOpen,
  Pencil,
  Trash2,
  MapPin,
  Briefcase,
  FileText,
  Calendar,
  Euro,
  Shield,
  GraduationCap,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { RapportFormateurPanel } from './rapports/RapportFormateurPanel';
import { ActiviteCollaborateurPanel } from '../components/ActiviteCollaborateurPanel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { ContratDocsActions } from '../components/formateur/ContratDocsActions';
import { formatNom, formatPrenom } from '../lib/personne';
import { CiviliteSelect } from '../components/CiviliteSelect';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const addMonthsStr = (dateStr, months) => {
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d)) return '';
  d.setMonth(d.getMonth() + months);
  return d.toLocaleDateString('fr-FR');
};

const TYPES_CONTRAT = [
  { value: 'vacataire', label: 'Vacataire', color: 'bg-amber-100 text-amber-700' },
  { value: 'cdd', label: 'CDD', color: 'bg-blue-100 text-blue-700' },
  { value: 'cdi', label: 'CDI', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'gerant', label: 'Gérant', color: 'bg-violet-100 text-violet-700' },
];

const emptyContrat = {
  type_contrat: 'vacataire',
  date_debut: '',
  date_fin: '',
  actif: true,
  adresse: '',
  code_postal: '',
  ville: '',
  tarif_horaire: '',
  tarif_journalier: '',
  numero_siret: '',
  attestation_urssaf: false,
  attestation_urssaf_date: '',
  casier_judiciaire_n3: false,
  casier_judiciaire_date: '',
  attestation_vigilance: false,
  attestation_vigilance_date: '',
  taux_horaire_brut: '',
  diplome: '',
  niveau_diplome: '',
  numero_securite_sociale: '',
  notes: '',
  poste: 'Animateur de formation',
  periode_essai_jours: '',
  date_dpae: '',
  objet_contrat: '',
  version_document: 'V01/2026',
  lieu_signature: '',
  date_signature: '',
  signe: false,
  charte_signee: false,
  charte_date_signature: ''
};

export const FormateursPage = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const [formateurs, setFormateurs] = useState([]);
  const [modules, setModules] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterContrat, setFilterContrat] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingFormateur, setEditingFormateur] = useState(null);
  const [selectedFormateur, setSelectedFormateur] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  
  // Contrat dialog
  const [contratDialogOpen, setContratDialogOpen] = useState(false);
  const [editingContrat, setEditingContrat] = useState(null);
  const [contratData, setContratData] = useState(emptyContrat);
  const [deleteContratTarget, setDeleteContratTarget] = useState(null);
  const [detailTab, setDetailTab] = useState('info');
  const [savingContrat, setSavingContrat] = useState(false);

  // Enregistre immédiatement la liste des contrats sur la fiche ouverte (onglet Contrats = seul lieu de gestion des contrats)
  const persistContrats = async (nouveauxContrats, message) => {
    const payload = cleanFormData({ ...selectedFormateur, contrats: nouveauxContrats });
    const res = await axios.put(`${API_URL}/formateurs/${selectedFormateur.id}`, payload);
    setFormateurs(prev => prev.map(f => f.id === selectedFormateur.id ? res.data : f));
    setSelectedFormateur(res.data);
    toast.success(message);
    return res.data;
  };

  const handleDeleteContratFromDetail = async () => {
    if (!selectedFormateur || !deleteContratTarget) return;
    try {
      await persistContrats((selectedFormateur.contrats || []).filter(c => c.id !== deleteContratTarget.id), 'Contrat supprimé');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleteContratTarget(null);
    }
  };
  
  const [formData, setFormData] = useState({
    civilite: '',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    telephone_portable: '',
    adresse: '',
    code_postal: '',
    ville: '',
    date_naissance: '',
    lieu_naissance: '',
    departement_naissance: '',
    nationalite: 'Française',
    numero_securite_sociale: '',
    modules_dispenses: [],
    specialites: '',
    notes: '',
    contrats: [],
    statut_intervenant: 'interne'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [formateursRes, modulesRes] = await Promise.all([
        axios.get(`${API_URL}/formateurs`),
        axios.get(`${API_URL}/modules`)
      ]);
      setFormateurs(formateursRes.data);
      setModules(modulesRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (formateur = null) => {
    if (formateur) {
      setEditingFormateur(formateur);
      setFormData({
        civilite: formateur.civilite || '',
        nom: formateur.nom || '',
        prenom: formateur.prenom || '',
        email: formateur.email || '',
        telephone: formateur.telephone || '',
        telephone_portable: formateur.telephone_portable || '',
        adresse: formateur.adresse || '',
        code_postal: formateur.code_postal || '',
        ville: formateur.ville || '',
        date_naissance: formateur.date_naissance || '',
        lieu_naissance: formateur.lieu_naissance || '',
        departement_naissance: formateur.departement_naissance || '',
        nationalite: formateur.nationalite || 'Française',
        numero_securite_sociale: formateur.numero_securite_sociale || (formateur.contrats || []).map(c => c.numero_securite_sociale).find(Boolean) || '',
        modules_dispenses: formateur.modules_dispenses || [],
        specialites: formateur.specialites || '',
        notes: formateur.notes || '',
        contrats: formateur.contrats || [],
        fonctions: formateur.fonctions || ['formateur'],
        statut_intervenant: formateur.statut_intervenant || 'interne'
      });
    } else {
      setEditingFormateur(null);
      setFormData({
        civilite: '',
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        telephone_portable: '',
        adresse: '',
        code_postal: '',
        ville: '',
        date_naissance: '',
        lieu_naissance: '',
        departement_naissance: '',
        nationalite: 'Française',
        numero_securite_sociale: '',
        modules_dispenses: [],
        specialites: '',
        notes: '',
        contrats: [],
        fonctions: ['formateur'],
        statut_intervenant: 'interne'
      });
    }
    setActiveTab('info');
    setDialogOpen(true);
  };

  const cleanFormData = (data) => {
    const cleaned = { ...data };
    // Clean contrats: convert empty strings to null for numeric fields
    if (cleaned.contrats) {
      cleaned.contrats = cleaned.contrats.map(c => ({
        ...c,
        periode_essai_jours: c.periode_essai_jours === '' || c.periode_essai_jours == null ? null : parseInt(c.periode_essai_jours, 10),
        date_dpae: c.date_dpae || null, objet_contrat: c.objet_contrat || null, lieu_signature: c.lieu_signature || null, date_signature: c.date_signature || null, charte_date_signature: c.charte_date_signature || null, poste: c.poste || null, version_document: c.version_document || null,
        tarif_horaire: c.tarif_horaire === '' ? null : c.tarif_horaire ? parseFloat(c.tarif_horaire) : null,
        tarif_journalier: c.tarif_journalier === '' ? null : c.tarif_journalier ? parseFloat(c.tarif_journalier) : null,
        taux_horaire_brut: c.taux_horaire_brut === '' ? null : c.taux_horaire_brut ? parseFloat(c.taux_horaire_brut) : null,
        date_fin: c.date_fin === '' ? null : c.date_fin,
        attestation_urssaf_date: c.attestation_urssaf_date === '' ? null : c.attestation_urssaf_date,
        casier_judiciaire_date: c.casier_judiciaire_date === '' ? null : c.casier_judiciaire_date,
        numero_siret: c.numero_siret === '' ? null : c.numero_siret,
        diplome: c.diplome === '' ? null : c.diplome,
        niveau_diplome: c.niveau_diplome === '' ? null : c.niveau_diplome,
        numero_securite_sociale: c.numero_securite_sociale === '' ? null : c.numero_securite_sociale,
        adresse: c.adresse === '' ? null : c.adresse,
        code_postal: c.code_postal === '' ? null : c.code_postal,
        ville: c.ville === '' ? null : c.ville,
        notes: c.notes === '' ? null : c.notes,
      }));
    }
    return cleaned;
  };

  const getErrorMessage = (error) => {
    const detail = error.response?.data?.detail;
    if (!detail) return 'Erreur lors de la sauvegarde';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(d => d.msg).join(', ');
    return 'Erreur lors de la sauvegarde';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = cleanFormData(formData);
      if (editingFormateur) {
        const res = await axios.put(`${API_URL}/formateurs/${editingFormateur.id}`, payload);
        setFormateurs(formateurs.map(f => f.id === editingFormateur.id ? res.data : f));
        if (selectedFormateur && selectedFormateur.id === editingFormateur.id) {
          setSelectedFormateur(res.data);
        }
        setEditingFormateur(res.data);
        toast.success('Collaborateur modifié — vous restez sur la fiche');
      } else {
        const res = await axios.post(`${API_URL}/formateurs`, payload);
        setFormateurs([...formateurs, res.data]);
        setEditingFormateur(res.data);
        toast.success('Collaborateur créé — vous pouvez compléter la fiche');
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/formateurs/${deleteId}`);
      setFormateurs(formateurs.filter(f => f.id !== deleteId));
      if (selectedFormateur && selectedFormateur.id === deleteId) {
        setSelectedFormateur(null);
      }
      toast.success('Collaborateur supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteId(null);
    }
  };

  const handleSelectFormateur = (formateur, tab = 'info') => {
    setDetailTab(tab);
    setSelectedFormateur(formateur);
  };

  const handleEditFromDetail = () => {
    if (selectedFormateur) {
      setSelectedFormateur(null);
      handleOpenDialog(selectedFormateur);
    }
  };

  // Depuis le formulaire d'édition : bascule vers l'onglet Contrats de la fiche (seul lieu de gestion des contrats)
  const handleGererContratsDepuisForm = () => {
    if (!editingFormateur) return;
    const fiche = formateurs.find(f => f.id === editingFormateur.id) || editingFormateur;
    setDialogOpen(false);
    handleSelectFormateur(fiche, 'contrats');
  };

  const handleDeleteFromDetail = () => {
    if (selectedFormateur) {
      setDeleteId(selectedFormateur.id);
    }
  };

  // Contrat management
  const handleOpenContratDialog = (contrat = null) => {
    if (contrat) {
      setEditingContrat(contrat);
      setContratData({ ...contrat });
    } else {
      setEditingContrat(null);
      setContratData({ 
        ...emptyContrat, 
        id: `contrat-${Date.now()}`,
        date_debut: format(new Date(), 'yyyy-MM-dd')
      });
    }
    setContratDialogOpen(true);
  };

  const handleSaveContrat = async () => {
    if (!contratData.date_debut) {
      toast.error('La date de début est obligatoire');
      return;
    }
    if (contratData.type_contrat === 'cdd' && !contratData.date_fin) {
      toast.error('La date de fin est obligatoire pour un CDD');
      return;
    }
    if (!selectedFormateur) return;
    const actuels = selectedFormateur.contrats || [];
    const newContrats = editingContrat
      ? actuels.map(c => (c.id === editingContrat.id ? { ...contratData } : c))
      : [...actuels, { ...contratData, id: contratData.id || `contrat-${Date.now()}` }];
    setSavingContrat(true);
    try {
      await persistContrats(newContrats, editingContrat ? 'Contrat modifié' : 'Contrat ajouté');
      setContratDialogOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingContrat(false);
    }
  };

  const toggleModule = (moduleId) => {
    const newModules = formData.modules_dispenses.includes(moduleId)
      ? formData.modules_dispenses.filter(id => id !== moduleId)
      : [...formData.modules_dispenses, moduleId];
    setFormData({ ...formData, modules_dispenses: newModules });
  };

  const getModuleName = (moduleId) => {
    const mod = modules.find(m => m.id === moduleId);
    return mod ? `${mod.code} - ${mod.titre}` : moduleId;
  };

  const getInitials = (nom, prenom) => {
    return `${prenom?.[0] || ''}${nom?.[0] || ''}`.toUpperCase();
  };

  const getContratActif = (formateur) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return formateur.contrats?.find(c => 
      c.actif && 
      c.date_debut <= today && 
      (!c.date_fin || c.date_fin >= today)
    );
  };

  const getTypeContratBadge = (type) => {
    const found = TYPES_CONTRAT.find(t => t.value === type);
    return found || { label: type, color: 'bg-slate-100 text-slate-700' };
  };

  const filteredFormateurs = formateurs.filter(formateur => {
    const matchesSearch = 
      formateur.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formateur.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formateur.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterContrat === 'all') return matchesSearch;
    if (filterContrat === 'externe') return matchesSearch && formateur.statut_intervenant === 'externe';
    
    const contratActif = getContratActif(formateur);
    if (filterContrat === 'sans_contrat') return matchesSearch && !contratActif;
    return matchesSearch && contratActif?.type_contrat === filterContrat;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="formateurs-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Collaborateurs</h1>
            <p className="text-slate-500 mt-1">{formateurs.length} collaborateur(s)</p>
          </div>
          <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-formateur-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau collaborateur
          </Button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher un collaborateur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-formateurs"
            />
          </div>
          <Select value={filterContrat} onValueChange={setFilterContrat}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filtrer par contrat" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="vacataire">Vacataires</SelectItem>
              <SelectItem value="cdd">CDD</SelectItem>
              <SelectItem value="cdi">CDI</SelectItem>
              <SelectItem value="sans_contrat">Sans contrat actif</SelectItem>
              <SelectItem value="externe">Formateurs extérieurs</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Formateurs List */}
        {loading ? (
          <Card><CardContent className="p-6 space-y-3">{[1, 2, 3].map((i) => (<div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />))}</CardContent></Card>
        ) : filteredFormateurs.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <UserCheck className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">Aucun collaborateur</h3>
              <p className="text-slate-500 mb-4">
                {searchTerm ? 'Aucun résultat pour cette recherche' : 'Ajoutez votre premier collaborateur'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm overflow-hidden">
            <div className="grid grid-cols-[auto_1fr_1fr_120px_auto_100px] gap-4 bg-slate-100 border-b px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">
              <span className="w-8"></span>
              <span>Nom</span>
              <span className="hidden md:block">Email</span>
              <span className="hidden md:block">Contrat</span>
              <span className="hidden lg:block">Fonctions</span>
              <span className="hidden lg:block">Modules</span>
            </div>
            <div className="divide-y">
              {filteredFormateurs.map((formateur) => {
                const contratActif = getContratActif(formateur);
                const typeBadge = contratActif ? getTypeContratBadge(contratActif.type_contrat) : null;
                const fonctionLabels = {formateur:'Form.', commercial:'Com.', referent_pedagogique:'Réf.P.', administratif:'Adm.', administrateur:'Admin.', testeur:'Test.', responsable_session:'Resp.S.'};
                const fonctionColors = {formateur:'bg-indigo-100 text-indigo-700', commercial:'bg-amber-100 text-amber-700', referent_pedagogique:'bg-purple-100 text-purple-700', administratif:'bg-sky-100 text-sky-700', administrateur:'bg-teal-100 text-teal-700', testeur:'bg-rose-100 text-rose-700', responsable_session:'bg-cyan-100 text-cyan-700'};
                return (
                  <div
                    key={formateur.id}
                    className="grid grid-cols-[auto_1fr_1fr_120px_auto_100px] gap-4 items-center px-5 py-3 hover:bg-slate-50 cursor-pointer transition-colors"
                    data-testid={`formateur-row-${formateur.id}`}
                    onClick={() => handleSelectFormateur(formateur)}
                  >
                    <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                      {getInitials(formateur.nom, formateur.prenom)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{formateur.nom} {formateur.prenom}</p>
                    </div>
                    <p className="text-sm text-slate-500 truncate hidden md:block">{formateur.email}</p>
                    <div className="hidden md:block">
                      {formateur.statut_intervenant === 'externe' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700" data-testid={`badge-externe-${formateur.id}`}>
                          Formateur extérieur
                        </span>
                      ) : contratActif ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${typeBadge.color}`}>
                          {typeBadge.label}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                          Sans contrat
                        </span>
                      )}
                    </div>
                    <div className="hidden lg:flex flex-wrap gap-1">
                      {(formateur.fonctions || ['formateur']).map(f => (
                        <span key={f} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${fonctionColors[f] || 'bg-slate-100 text-slate-700'}`}>{fonctionLabels[f] || f}</span>
                      ))}
                    </div>
                    <p className="text-sm text-slate-500 hidden lg:block">{formateur.modules_dispenses?.length || 0} module(s)</p>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Formateur Detail Dialog */}
        <Dialog open={!!selectedFormateur} onOpenChange={(open) => !open && setSelectedFormateur(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedFormateur && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center text-white text-xl font-semibold">
                      {getInitials(selectedFormateur.nom, selectedFormateur.prenom)}
                    </div>
                    <div>
                      <DialogTitle className="text-2xl font-['Plus_Jakarta_Sans']">
                        {selectedFormateur.nom} {selectedFormateur.prenom}
                      </DialogTitle>
                      {(() => {
                        const contrat = getContratActif(selectedFormateur);
                        if (contrat) {
                          const badge = getTypeContratBadge(contrat.type_contrat);
                          return (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded text-sm font-medium mt-1 ${badge.color}`}>
                              {badge.label} actif
                            </span>
                          );
                        }
                        return (
                          <span className="inline-flex items-center px-2.5 py-1 rounded text-sm font-medium mt-1 bg-red-100 text-red-700">
                            Aucun contrat actif
                          </span>
                        );
                      })()}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(selectedFormateur.fonctions || ['formateur']).map(f => {
                          const labels = {formateur:'Formateur', commercial:'Commercial', referent_pedagogique:'Réf. pédagogique', administratif:'Administratif', administrateur:'Administrateur', testeur:'Testeur', responsable_session:'Responsable de session'};
                          const colors = {formateur:'bg-indigo-100 text-indigo-700', commercial:'bg-amber-100 text-amber-700', referent_pedagogique:'bg-purple-100 text-purple-700', administrateur:'bg-teal-100 text-teal-700', testeur:'bg-rose-100 text-rose-700', responsable_session:'bg-cyan-100 text-cyan-700'};
                          return <span key={f} className={`px-2 py-0.5 rounded text-xs font-medium ${colors[f] || 'bg-slate-100 text-slate-700'}`}>{labels[f] || f}</span>;
                        })}
                      </div>
                    </div>
                  </div>
                </DialogHeader>
                
                <Tabs value={detailTab} onValueChange={setDetailTab} className="mt-4">
                  <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-5' : 'grid-cols-3'}`}>
                    <TabsTrigger value="info">Informations</TabsTrigger>
                    {isAdmin && (
                      <TabsTrigger value="contrats" data-testid="tab-contrats-fiche">
                        Contrats ({selectedFormateur.contrats?.length || 0})
                      </TabsTrigger>
                    )}
                    <TabsTrigger value="modules">
                      Modules ({selectedFormateur.modules_dispenses?.length || 0})
                    </TabsTrigger>
                    {isAdmin && <TabsTrigger value="activite" data-testid="tab-activite-formateur">Activité</TabsTrigger>}
                    <TabsTrigger value="rapport" data-testid="tab-rapport-formateur">Rapport</TabsTrigger>
                  </TabsList>

                  {isAdmin && (
                    <TabsContent value="activite" className="mt-4">
                      <ActiviteCollaborateurPanel formateurId={selectedFormateur.id} />
                    </TabsContent>
                  )}

                  <TabsContent value="rapport" className="mt-4">
                    <RapportFormateurPanel formateurId={selectedFormateur.id} />
                  </TabsContent>
                  
                  <TabsContent value="info" className="space-y-4 mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <Mail className="w-5 h-5 text-slate-600 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-slate-500">Email</p>
                          <p className="text-slate-900">{selectedFormateur.email}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <Phone className="w-5 h-5 text-slate-600 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-slate-500">Téléphone</p>
                          <p className="text-slate-900">{selectedFormateur.telephone || '-'}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg md:col-span-2">
                        <MapPin className="w-5 h-5 text-slate-600 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-slate-500">Adresse</p>
                          <p className="text-slate-900">
                            {selectedFormateur.adresse || '-'}
                            {selectedFormateur.code_postal && `, ${selectedFormateur.code_postal}`}
                            {selectedFormateur.ville && ` ${selectedFormateur.ville}`}
                          </p>
                        </div>
                      </div>
                    </div>
                    {selectedFormateur.specialites && (
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs font-medium text-slate-500 mb-1">Spécialités</p>
                        <p className="text-slate-700">{selectedFormateur.specialites}</p>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="contrats" className="space-y-4 mt-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm text-slate-500">Ajoutez, modifiez, supprimez les contrats et déclarez leur signature ici — chaque action est enregistrée immédiatement.</p>
                      {selectedFormateur.statut_intervenant !== 'externe' && (
                        <Button type="button" onClick={() => handleOpenContratDialog()} className="bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid="btn-ajouter-contrat">
                          <Plus className="w-4 h-4 mr-2" />
                          Ajouter un contrat
                        </Button>
                      )}
                    </div>
                    {selectedFormateur.contrats?.length > 0 ? (
                      <div className="space-y-3">
                        {selectedFormateur.contrats.map((contrat) => {
                          const badge = getTypeContratBadge(contrat.type_contrat);
                          const isActif = contrat.actif && 
                            contrat.date_debut <= format(new Date(), 'yyyy-MM-dd') && 
                            (!contrat.date_fin || contrat.date_fin >= format(new Date(), 'yyyy-MM-dd'));
                          
                          return (
                            <div key={contrat.id} className={`p-4 rounded-lg border ${isActif ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50'}`}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>
                                    {badge.label}
                                  </span>
                                  {isActif && (
                                    <span className="flex items-center gap-1 text-xs text-emerald-600">
                                      <CheckCircle className="w-3 h-3" /> Actif
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-slate-500">
                                    {contrat.date_debut && format(parseISO(contrat.date_debut), 'dd/MM/yyyy')}
                                    {contrat.date_fin && ` → ${format(parseISO(contrat.date_fin), 'dd/MM/yyyy')}`}
                                  </span>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-slate-500 hover:text-indigo-700 hover:bg-indigo-50"
                                    title="Modifier ce contrat"
                                    onClick={() => handleOpenContratDialog(contrat)}
                                    data-testid={`btn-edit-contrat-${contrat.id}`}
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    title="Supprimer ce contrat"
                                    onClick={() => setDeleteContratTarget(contrat)}
                                    data-testid={`btn-delete-contrat-${contrat.id}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                                {contrat.tarif_journalier && (
                                  <div>
                                    <span className="text-slate-500">Tarif jour:</span>
                                    <span className="ml-1 font-medium">{contrat.tarif_journalier}€</span>
                                  </div>
                                )}
                                {contrat.tarif_horaire && (
                                  <div>
                                    <span className="text-slate-500">Tarif heure:</span>
                                    <span className="ml-1 font-medium">{contrat.tarif_horaire}€</span>
                                  </div>
                                )}
                                {contrat.taux_horaire_brut && (
                                  <div>
                                    <span className="text-slate-500">Brut salarial:</span>
                                    <span className="ml-1 font-medium">{contrat.taux_horaire_brut}€/h</span>
                                  </div>
                                )}
                              </div>
                              
                              {contrat.type_contrat !== 'vacataire' && (
                                <ContratDocsActions formateur={selectedFormateur} contrat={contrat} onSaved={async () => { try { const r = await axios.get(`${API_URL}/formateurs/${selectedFormateur.id}`); setSelectedFormateur(r.data); setFormateurs(prev => prev.map(f => f.id === r.data.id ? r.data : f)); } catch { /* noop */ } }} />
                              )}
                              {contrat.type_contrat === 'vacataire' && (
                                <div className="mt-2 pt-2 border-t flex flex-wrap gap-2">
                                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${contrat.attestation_urssaf ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                    {contrat.attestation_urssaf ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                    URSSAF
                                  </span>
                                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${contrat.casier_judiciaire_n3 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                    {contrat.casier_judiciaire_n3 ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                    Casier N°3
                                  </span>
                                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${contrat.attestation_vigilance ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                    {contrat.attestation_vigilance ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                    Vigilance
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                        <p>Aucun contrat enregistré</p>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="modules" className="mt-4">
                    {selectedFormateur.modules_dispenses?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedFormateur.modules_dispenses.map((moduleId) => (
                          <div key={moduleId} className="flex items-center gap-2 p-2 bg-indigo-50 rounded border border-indigo-200">
                            <BookOpen className="w-4 h-4 text-indigo-600" />
                            <span className="text-sm text-indigo-900">{getModuleName(moduleId)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                        <p>Aucun module assigné</p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>

                <DialogFooter className="flex gap-2 sm:gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                    onClick={handleDeleteFromDetail}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                  <Button 
                    className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={handleEditFromDetail}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Modifier
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Formateur Form Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <DialogTitle className="font-['Plus_Jakarta_Sans']">
                    {editingFormateur ? 'Modifier le collaborateur' : 'Nouveau collaborateur'}
                  </DialogTitle>
                  <DialogDescription>
                    Gérez les informations et contrats du collaborateur
                  </DialogDescription>
                </div>
                <div className="flex items-center gap-2 mr-6">
                  {isAdmin && editingFormateur && formData.statut_intervenant !== 'externe' && (
                    <Button type="button" variant="outline" size="sm" onClick={handleGererContratsDepuisForm} data-testid="btn-gerer-contrats">
                      <Briefcase className="w-4 h-4 mr-2" />
                      Contrats ({(formateurs.find(f => f.id === editingFormateur.id)?.contrats || []).length})
                    </Button>
                  )}
                  <Button type="submit" form="formateur-form" size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-formateur-top">
                    {editingFormateur ? 'Mettre à jour' : 'Créer'}
                  </Button>
                </div>
              </div>
            </DialogHeader>
            
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList className={`grid w-full ${isAdmin && editingFormateur && formData.statut_intervenant !== 'externe' ? 'grid-cols-3' : 'grid-cols-2'}`}>
                <TabsTrigger value="info">Informations</TabsTrigger>
                <TabsTrigger value="modules">Modules</TabsTrigger>
                {isAdmin && editingFormateur && formData.statut_intervenant !== 'externe' && <TabsTrigger value="activite" data-testid="tab-activite-edit">Activité</TabsTrigger>}
              </TabsList>

              {isAdmin && editingFormateur && (
                <TabsContent value="activite" className="mt-4">
                  <ActiviteCollaborateurPanel formateurId={editingFormateur.id} />
                </TabsContent>
              )}
              
              <form id="formateur-form" onSubmit={handleSubmit}>
                <TabsContent value="info" className="space-y-6 mt-4">
                  <div className="grid grid-cols-[110px_1fr_1fr] gap-4">
                    <CiviliteSelect value={formData.civilite} onChange={(v) => setFormData({ ...formData, civilite: v })} testId="formateur-civilite" />
                    <div className="space-y-2">
                      <Label htmlFor="prenom">Prénom *</Label>
                      <Input
                        id="prenom"
                        value={formData.prenom}
                        onChange={(e) => setFormData({ ...formData, prenom: formatPrenom(e.target.value) })}
                        required
                        data-testid="input-formateur-prenom"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nom">Nom *</Label>
                      <Input
                        id="nom"
                        value={formData.nom}
                        onChange={(e) => setFormData({ ...formData, nom: formatNom(e.target.value) })}
                        data-testid="input-formateur-nom"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>

                  <div className={`rounded-lg border p-3 flex items-start gap-3 ${formData.statut_intervenant === 'externe' ? 'border-violet-300 bg-violet-50' : 'border-slate-200 bg-slate-50'}`} data-testid="statut-intervenant-bloc">
                    <input type="checkbox" id="statut-externe" className="mt-1 h-4 w-4 accent-violet-600" checked={formData.statut_intervenant === 'externe'}
                      onChange={(e) => setFormData({ ...formData, statut_intervenant: e.target.checked ? 'externe' : 'interne' })} data-testid="checkbox-formateur-externe" />
                    <label htmlFor="statut-externe" className="text-sm cursor-pointer">
                      <span className="font-medium text-slate-800">Formateur extérieur</span>
                      <p className="text-xs text-slate-500 mt-0.5">Intervenant non salarié : aucun contrat, pas de paie ni de facturation de ses journées aux financeurs. Il intervient dans le parcours et saisit ses journées (cahier de texte, présences, évaluations) comme les formateurs internes.</p>
                    </label>
                  </div>

                  <div className="space-y-2">
                    <Label>Fonctions *</Label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: 'formateur', label: 'Formateur', color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
                        { value: 'commercial', label: 'Commercial', color: 'bg-amber-100 text-amber-700 border-amber-300' },
                        { value: 'referent_pedagogique', label: 'Réf. pédagogique', color: 'bg-purple-100 text-purple-700 border-purple-300' },
                        { value: 'administratif', label: 'Administratif', color: 'bg-sky-100 text-sky-700 border-sky-300' },
                        { value: 'administrateur', label: 'Administrateur', color: 'bg-teal-100 text-teal-700 border-teal-300' },
                        { value: 'testeur', label: 'Testeur', color: 'bg-rose-100 text-rose-700 border-rose-300' },
                        { value: 'responsable_session', label: 'Responsable de session', color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
                      ].map(fn => {
                        const selected = (formData.fonctions || []).includes(fn.value);
                        return (
                          <button
                            key={fn.value}
                            type="button"
                            data-testid={`fn-${fn.value}`}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${selected ? fn.color + ' ring-2 ring-offset-1 ring-current' : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100'}`}
                            onClick={() => {
                              const fns = formData.fonctions || [];
                              setFormData({ ...formData, fonctions: selected ? fns.filter(f => f !== fn.value) : [...fns, fn.value] });
                            }}
                          >
                            {selected ? '✓ ' : ''}{fn.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="telephone">Téléphone fixe</Label>
                      <Input
                        id="telephone"
                        value={formData.telephone}
                        onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telephone_portable">Téléphone portable</Label>
                      <Input
                        id="telephone_portable"
                        value={formData.telephone_portable}
                        onChange={(e) => setFormData({ ...formData, telephone_portable: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="adresse">Adresse</Label>
                    <Input
                      id="adresse"
                      value={formData.adresse}
                      onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="code_postal">Code postal</Label>
                      <Input
                        id="code_postal"
                        value={formData.code_postal}
                        onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ville">Ville</Label>
                      <Input
                        id="ville"
                        value={formData.ville}
                        onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 rounded-lg bg-slate-50 border" data-testid="formateur-naissance-bloc">
                    <div className="space-y-2">
                      <Label htmlFor="date_naissance">Date de naissance</Label>
                      <Input id="date_naissance" type="date" value={formData.date_naissance || ''} onChange={(e) => setFormData({ ...formData, date_naissance: e.target.value })} data-testid="formateur-date-naissance" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lieu_naissance">Lieu de naissance</Label>
                      <Input id="lieu_naissance" value={formData.lieu_naissance || ''} onChange={(e) => setFormData({ ...formData, lieu_naissance: e.target.value })} placeholder="Ville" data-testid="formateur-lieu-naissance" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="departement_naissance">Département de naissance</Label>
                      <Input id="departement_naissance" value={formData.departement_naissance || ''} onChange={(e) => setFormData({ ...formData, departement_naissance: e.target.value })} placeholder="ex. 33" data-testid="formateur-departement-naissance" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nationalite">Nationalité</Label>
                      <Input id="nationalite" value={formData.nationalite || ''} onChange={(e) => setFormData({ ...formData, nationalite: e.target.value })} data-testid="formateur-nationalite" />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="nss">N° de sécurité sociale</Label>
                      <Input id="nss" value={formData.numero_securite_sociale || ''} onChange={(e) => setFormData({ ...formData, numero_securite_sociale: e.target.value })} placeholder="1 85 05 33 063 000 00" data-testid="formateur-nss" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="specialites">Spécialités</Label>
                    <Textarea
                      id="specialites"
                      value={formData.specialites}
                      onChange={(e) => setFormData({ ...formData, specialites: e.target.value })}
                      rows={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={2}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="modules" className="space-y-4 mt-4">
                  <p className="text-sm text-slate-500">
                    Sélectionnez les modules que ce formateur peut dispenser
                  </p>
                  <div className="max-h-[400px] overflow-y-auto border rounded-lg p-2">
                    {modules.length === 0 ? (
                      <p className="text-center py-4 text-slate-500">Aucun module disponible</p>
                    ) : (
                      <div className="space-y-1">
                        {modules.map((module) => (
                          <div
                            key={module.id}
                            className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-slate-50 ${
                              formData.modules_dispenses.includes(module.id) ? 'bg-indigo-50' : ''
                            }`}
                            onClick={() => toggleModule(module.id)}
                          >
                            <Checkbox
                              checked={formData.modules_dispenses.includes(module.id)}
                              onCheckedChange={() => toggleModule(module.id)}
                            />
                            <span className="text-xs font-mono text-indigo-600">{module.code}</span>
                            <span className="text-sm text-slate-700">{module.titre}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                    {editingFormateur ? 'Mettre à jour' : 'Créer'}
                  </Button>
                </DialogFooter>
              </form>
            </Tabs>
          </DialogContent>
        </Dialog>

        {/* Contrat Form Dialog */}
        <Dialog open={contratDialogOpen} onOpenChange={setContratDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Plus_Jakarta_Sans']">
                {editingContrat ? 'Modifier le contrat' : 'Nouveau contrat'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type de contrat *</Label>
                  <Select
                    value={contratData.type_contrat}
                    onValueChange={(value) => setContratData({ ...contratData, type_contrat: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPES_CONTRAT.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2 pt-6">
                  <Checkbox
                    id="actif"
                    checked={contratData.actif}
                    onCheckedChange={(checked) => setContratData({ ...contratData, actif: checked })}
                  />
                  <label htmlFor="actif" className="text-sm font-medium">
                    Contrat actif
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de début {contratData.type_contrat !== 'gerant' && '*'}</Label>
                  <Input
                    type="date"
                    value={contratData.date_debut || ''}
                    onChange={(e) => setContratData({ ...contratData, date_debut: e.target.value })}
                    required={contratData.type_contrat !== 'gerant'}
                    data-testid="contrat-date-debut"
                  />
                  {contratData.type_contrat === 'gerant' && <p className="text-[11px] text-slate-500">Le gérant n'est soumis ni à une durée de contrat ni aux activités journalières.</p>}
                  {contratData.type_contrat === 'cdi' && <p className="text-[11px] text-slate-500">Actif en permanence tant que le contrat n'est pas rendu inactif.</p>}
                  {contratData.type_contrat === 'cdd' && <p className="text-[11px] text-slate-500">Actif uniquement entre la date de début et la date de fin.</p>}
                  {contratData.type_contrat === 'vacataire' && <p className="text-[11px] text-slate-500">Actif du début du contrat jusqu'à l'échéance du premier document Qualiopi caduc (URSSAF/vigilance : 6 mois, casier : 3 mois).</p>}
                </div>
                <div className="space-y-2">
                  <Label>Date de fin {contratData.type_contrat === 'cdd' && '*'}</Label>
                  <Input
                    type="date"
                    value={contratData.date_fin || ''}
                    onChange={(e) => setContratData({ ...contratData, date_fin: e.target.value })}
                    required={contratData.type_contrat === 'cdd'}
                  />
                </div>
              </div>

              {/* Champs spécifiques Vacataire */}
              {contratData.type_contrat === 'vacataire' && (
                <>
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <Euro className="w-4 h-4" /> Tarification
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Tarif horaire (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={contratData.tarif_horaire || ''}
                          onChange={(e) => setContratData({ ...contratData, tarif_horaire: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Tarif journalier (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={contratData.tarif_journalier || ''}
                          onChange={(e) => setContratData({ ...contratData, tarif_journalier: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2 mt-4">
                      <Label>N° SIRET</Label>
                      <Input
                        value={contratData.numero_siret || ''}
                        onChange={(e) => setContratData({ ...contratData, numero_siret: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Documents Qualiopi
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={contratData.attestation_urssaf}
                            onCheckedChange={(checked) => setContratData({ ...contratData, attestation_urssaf: checked })}
                          />
                          <span className="text-sm">Attestation URSSAF</span>
                        </div>
                        <Input
                          type="date"
                          value={contratData.attestation_urssaf_date || ''}
                          onChange={(e) => setContratData({ ...contratData, attestation_urssaf_date: e.target.value })}
                          className="w-40"
                          placeholder="Date"
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={contratData.casier_judiciaire_n3}
                            onCheckedChange={(checked) => setContratData({ ...contratData, casier_judiciaire_n3: checked })}
                          />
                          <span className="text-sm">Casier Judiciaire N°3</span>
                        </div>
                        <Input
                          type="date"
                          value={contratData.casier_judiciaire_date || ''}
                          onChange={(e) => setContratData({ ...contratData, casier_judiciaire_date: e.target.value })}
                          className="w-40"
                          placeholder="Date"
                        />
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                        <Checkbox
                          checked={contratData.attestation_vigilance}
                          onCheckedChange={(checked) => setContratData({ ...contratData, attestation_vigilance: checked })}
                        />
                        <span className="text-sm">Attestation de vigilance</span>
                        <Input
                          type="date"
                          value={contratData.attestation_vigilance_date || ''}
                          onChange={(e) => setContratData({ ...contratData, attestation_vigilance_date: e.target.value })}
                          className="w-40"
                          placeholder="Date"
                          data-testid="contrat-vigilance-date"
                        />
                      </div>
                      {(contratData.attestation_urssaf_date || contratData.casier_judiciaire_date || contratData.attestation_vigilance_date) && (
                        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="contrat-echeances">
                          Échéances calculées : {[
                            contratData.attestation_urssaf_date && `URSSAF → ${addMonthsStr(contratData.attestation_urssaf_date, 6)}`,
                            contratData.attestation_vigilance_date && `Vigilance → ${addMonthsStr(contratData.attestation_vigilance_date, 6)}`,
                            contratData.casier_judiciaire_date && `Casier → ${addMonthsStr(contratData.casier_judiciaire_date, 3)}`,
                          ].filter(Boolean).join(' · ')}. Le collaborateur devient inactif à la première échéance.
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Champs spécifiques CDD/CDI */}
              {(contratData.type_contrat === 'cdd' || contratData.type_contrat === 'cdi') && (
                <>
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Édition du contrat
                    </h4>
                    <div className="grid grid-cols-2 gap-4 mb-4" data-testid="contrat-edition-bloc">
                      <div className="space-y-2"><Label>Poste / qualité</Label><Input value={contratData.poste || ''} onChange={(e) => setContratData({ ...contratData, poste: e.target.value })} placeholder="Animateur de formation" data-testid="contrat-poste" /></div>
                      <div className="space-y-2"><Label>Période d'essai (jours)</Label><Input type="number" value={contratData.periode_essai_jours ?? ''} onChange={(e) => setContratData({ ...contratData, periode_essai_jours: e.target.value })} placeholder="10" data-testid="contrat-essai" /></div>
                      <div className="space-y-2"><Label>Date DPAE (URSSAF)</Label><Input type="date" value={contratData.date_dpae || ''} onChange={(e) => setContratData({ ...contratData, date_dpae: e.target.value })} data-testid="contrat-dpae" /></div>
                      <div className="space-y-2"><Label>Version du document</Label><Input value={contratData.version_document || ''} onChange={(e) => setContratData({ ...contratData, version_document: e.target.value })} placeholder="V01/2026" data-testid="contrat-version" /></div>
                      <div className="space-y-2 col-span-2"><Label>Objet du contrat (motif du CDD d'usage)</Label><Textarea rows={2} value={contratData.objet_contrat || ''} onChange={(e) => setContratData({ ...contratData, objet_contrat: e.target.value })} placeholder="en réponse aux besoins des actions de formation … dans les métiers de la logistique" data-testid="contrat-objet" /></div>
                    </div>
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <Euro className="w-4 h-4" /> Rémunération
                    </h4>
                    <div className="space-y-2">
                      <Label>Taux horaire brut salarial (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={contratData.taux_horaire_brut || ''}
                        onChange={(e) => setContratData({ ...contratData, taux_horaire_brut: e.target.value })}
                        data-testid="contrat-taux-horaire-brut"
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <GraduationCap className="w-4 h-4" /> Formation / Diplôme
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Diplôme</Label>
                        <Input
                          value={contratData.diplome || ''}
                          onChange={(e) => setContratData({ ...contratData, diplome: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Niveau</Label>
                        <Select
                          value={contratData.niveau_diplome || ''}
                          onValueChange={(value) => setContratData({ ...contratData, niveau_diplome: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bac">Bac</SelectItem>
                            <SelectItem value="bac+2">Bac+2</SelectItem>
                            <SelectItem value="bac+3">Bac+3</SelectItem>
                            <SelectItem value="bac+5">Bac+5</SelectItem>
                            <SelectItem value="doctorat">Doctorat</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Adresse (pour ce contrat)
                </h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Adresse</Label>
                    <Input
                      value={contratData.adresse || ''}
                      onChange={(e) => setContratData({ ...contratData, adresse: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Code postal</Label>
                      <Input
                        value={contratData.code_postal || ''}
                        onChange={(e) => setContratData({ ...contratData, code_postal: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ville</Label>
                      <Input
                        value={contratData.ville || ''}
                        onChange={(e) => setContratData({ ...contratData, ville: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={contratData.notes || ''}
                  onChange={(e) => setContratData({ ...contratData, notes: e.target.value })}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setContratDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                type="button" 
                onClick={handleSaveContrat}
                disabled={savingContrat}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-save-contrat"
              >
                {editingContrat ? 'Mettre à jour' : 'Ajouter le contrat'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce formateur ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Le formateur et tous ses contrats seront supprimés.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Contrat Confirmation */}
        <AlertDialog open={!!deleteContratTarget} onOpenChange={(o) => !o && setDeleteContratTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce contrat ?</AlertDialogTitle>
              <AlertDialogDescription>
                Le contrat {deleteContratTarget?.type_contrat ? `(${deleteContratTarget.type_contrat.toUpperCase()}) ` : ''}
                débutant le {deleteContratTarget?.date_debut ? format(parseISO(deleteContratTarget.date_debut), 'dd/MM/yyyy') : '—'} sera définitivement supprimé.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteContratFromDetail} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-contrat">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};
